import type { Cents } from './money';
import type { ISODate } from './dates';

export type EntryKind = 'payable' | 'receivable';

/** Estado financeiro — independente do estado de aprovação. */
export type EntryStatus = 'open' | 'partial' | 'settled' | 'canceled';

/** Estado da autorização interna (apenas contas a pagar). */
export type ApprovalStatus = 'not_requested' | 'pending' | 'approved' | 'rejected' | 'invalidated';

export type Priority = 'low' | 'normal' | 'high' | 'urgent';

export const ENTRY_STATUS_LABEL: Record<EntryKind, Record<EntryStatus, string>> = {
  payable: { open: 'Em aberto', partial: 'Pago parcialmente', settled: 'Pago', canceled: 'Cancelado' },
  receivable: { open: 'A receber', partial: 'Recebido parcialmente', settled: 'Recebido', canceled: 'Cancelado' },
};

export const APPROVAL_STATUS_LABEL: Record<ApprovalStatus, string> = {
  not_requested: 'Aprovação não solicitada',
  pending: 'Aguardando autorização',
  approved: 'Autorizado',
  rejected: 'Recusado',
  invalidated: 'Autorização invalidada',
};

export const PRIORITY_LABEL: Record<Priority, string> = {
  low: 'Baixa',
  normal: 'Normal',
  high: 'Alta',
  urgent: 'Imediata',
};

export interface SettlementParts {
  principalCents: Cents;
  interestCents: Cents;
  fineCents: Cents;
  discountCents: Cents;
}

/** Valor que efetivamente sai/entra no banco. */
export function settlementCash(p: SettlementParts): Cents {
  return p.principalCents + p.interestCents + p.fineCents;
}

/** Quanto a baixa abate do saldo da obrigação (principal + desconto/ajuste). */
export function settlementReduction(p: SettlementParts): Cents {
  return p.principalCents + p.discountCents;
}

export interface BalanceSummary {
  originalCents: Cents | null;
  settledCents: Cents; // abatido do saldo (principal + descontos/ajustes)
  cashCents: Cents; // total liquidado em caixa (inclui juros e multa)
  interestCents: Cents;
  fineCents: Cents;
  discountCents: Cents;
  remainingCents: Cents | null; // null quando o valor original ainda não foi informado
}

export function summarizeBalance(
  originalCents: Cents | null,
  settlements: Array<SettlementParts & { reversed: boolean }>,
): BalanceSummary {
  let settled = 0;
  let cash = 0;
  let interest = 0;
  let fine = 0;
  let discount = 0;
  for (const s of settlements) {
    if (s.reversed) continue;
    settled += settlementReduction(s);
    cash += settlementCash(s);
    interest += s.interestCents;
    fine += s.fineCents;
    discount += s.discountCents;
  }
  return {
    originalCents,
    settledCents: settled,
    cashCents: cash,
    interestCents: interest,
    fineCents: fine,
    discountCents: discount,
    remainingCents: originalCents === null ? null : originalCents - settled,
  };
}

/** Estado financeiro derivado dos registros válidos. */
export function deriveEntryStatus(
  originalCents: Cents | null,
  settledCents: Cents,
  canceled: boolean,
): EntryStatus {
  if (canceled) return 'canceled';
  if (originalCents === null || settledCents <= 0) return 'open';
  if (settledCents >= originalCents) return 'settled';
  return 'partial';
}

export function isOverdue(status: EntryStatus, dueDate: ISODate | null, today: ISODate): boolean {
  if (!dueDate) return false;
  return (status === 'open' || status === 'partial') && dueDate < today;
}

/** Campos cuja alteração invalida uma autorização já concedida ou pendente. */
export const APPROVAL_SENSITIVE_FIELDS = ['amountCents', 'partyId', 'bankAccountId', 'dueDate'] as const;
